/**
 * Admin Results tab for FPR games.
 *
 * Renders:
 *   - Cumulative leaderboard at the top (reuses calculateFprGameFinalScores)
 *   - A grid of per-round breakdowns (newest first), each showing the rich
 *     per-team detail (boss kills with UTC, authoritative player, timezones)
 *   - A chronological audit log on the right
 *
 * Live: re-fetches the admin Results snapshot when relevant FPR broadcasts
 * arrive (wired in FprContext via scheduleFprAdminResultsRefresh).
 */

import { useEffect } from 'react';
import {
  calculateFprGameFinalScores,
  FLASHPOINT_DIFFICULTY_LABELS,
  MEDALS,
  type FlashpointDifficulty,
} from '@swtorbit/shared-socket-events';
import { useGameContext } from '../../contexts/GameContext';
import { useFprContext } from '../../contexts/FprContext';
import { ResultsTabBase } from '../results';
import { FprResultsTeamCard } from './FprResultsTeamCard';
import { FprResultsAuditLog } from './FprResultsAuditLog';
import { statusBadgeClass, leaderboardRowClass } from '@swtorbit/shared-components';

export function FprResultsTab() {
  const { selectedGame } = useGameContext();
  const { fprAdminResults, refreshFprAdminResults } = useFprContext();

  useEffect(() => {
    if (selectedGame) refreshFprAdminResults();
  }, [selectedGame?.dbId, refreshFprAdminResults]);

  const gameScores =
    selectedGame && selectedGame.rounds.some((r) => r.status === 'completed')
      ? calculateFprGameFinalScores(selectedGame)
      : null;
  const isCompleted = selectedGame?.status === 'completed';

  return (
    <ResultsTabBase
      hasSelectedGame={!!selectedGame}
      game={selectedGame}
      title="🏁 Results"
      onRefresh={refreshFprAdminResults}
      lastRefreshedUtcMs={fprAdminResults?.generatedAtUtcMs}
    >
      {/* Cumulative leaderboard */}
      {gameScores && (
        <div className={`p-lg border-md rounded-base${isCompleted ? ' border-gold glow glow-gold' : ' border-blue-light glow glow-blue-light'}`}>
          <div className="d-flex align-items-center justify-content-center mb-sm gap-sm">
            {isCompleted && <span className="fs-lg">🏆</span>}
            <h3 className={isCompleted ? 'text-gold' : 'text-white'}>
              {isCompleted ? 'Game Results' : 'Current Standings'}
            </h3>
          </div>
          <div className="d-flex flex-column gap-xs">
            {gameScores.leaderboard.map((team, idx) => (
              <div
                key={team.teamId}
                className={leaderboardRowClass(idx)}
              >
                <span className="leaderboard__rank">{idx < 3 ? MEDALS[idx] : `${idx + 1}.`}</span>
                <span className="leaderboard__name">{team.teamName}</span>
                <span className="leaderboard__stats">
                  {team.roundsPlayed} {team.roundsPlayed === 1 ? 'round' : 'rounds'}
                </span>
                <span className="leaderboard__score">{team.totalScore} pts</span>
              </div>
            ))}
          </div>
          <div className="leaderboard__footer">
            <p>Total Rounds: {gameScores.totalRounds}</p>
          </div>
        </div>
      )}

      {/* Snapshot body — rounds + audit log */}
      {!fprAdminResults ? (
        <div className="bg-space bg-o-50 text-cyan p-sm rounded-base border-md border-blue-light">
          Loading round details…
        </div>
      ) : (
        <div className="d-flex flex-column flex-lg-row gap-sm">
          {/* Per-round breakdowns */}
          <div className="d-flex flex-column gap-sm flex-1">
            {fprAdminResults.rounds.length === 0 ? (
              <div className="bg-space bg-o-50 text-cyan p-sm rounded-base border-md border-blue-light">
                No rounds have been created yet.
              </div>
            ) : (
              fprAdminResults.rounds.map((round) => (
                <div
                  key={round.roundId}
                  className="bg-space bg-o-50 text-white border-md border-blue-light rounded-base p-sm d-flex flex-column gap-xs"
                >
                  <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
                    <h4 className="text-cyan">
                      Round {round.roundNumber}
                      {round.flashpoint && (
                        <span className="text-slate ff-display fs-sm ms-xs">
                          {round.flashpoint.name} (
                          {FLASHPOINT_DIFFICULTY_LABELS[round.flashpoint.difficulty as FlashpointDifficulty] ??
                            round.flashpoint.difficulty}
                          )
                        </span>
                      )}
                    </h4>
                    <span className={statusBadgeClass(round.status)}>
                      {round.status.toUpperCase()}
                    </span>
                  </div>

                  {round.teams.length === 0 ? (
                    <span className="text-cyan fs-sm">No team data for this round.</span>
                  ) : (
                    <div className="d-flex flex-column gap-xs">
                      {round.teams.map((team) => (
                        <FprResultsTeamCard key={team.teamId} team={team} />
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Audit log */}
          <div className="flex-1">
            <FprResultsAuditLog results={fprAdminResults} />
          </div>
        </div>
      )}
    </ResultsTabBase>
  );
}
